/**
 * SPA UI sound effects via WebAudio. No sample files: each sound is a
 * sequence of sine bursts described by a SoundPreset in
 * @slaplink/shared, so admins can swap presets and offline / slow-link
 * users still get audio.
 *
 * Two events fire sounds:
 *   - playShareDing()        - confirmation after a share-send.
 *   - playNotificationDing() - inbound notification arrived (WS push).
 *
 * Resolution order for which preset to play:
 *   1. The SystemSettings.soundShareTone / soundNotificationTone slug
 *      (admin-controlled, exposed via /api/public-config).
 *   2. Hard-coded fallback in case the public-config hasn't loaded yet
 *      ('ascending' for share, 'three-tone' for notification).
 *
 * On/off resolution:
 *   1. localStorage `sl.sound.enabled` if the user has explicitly set it.
 *   2. SystemSettings.soundsEnabledByDefault (admin-controlled default
 *      for users who haven't toggled themselves).
 *
 * Browsers require a user gesture before the first AudioContext play.
 * Share-confirm always runs after a click → fine. Inbound notifications
 * may have no recent gesture; modern browsers allow it once any gesture
 * has happened in the tab, otherwise the play silently no-ops.
 */
import {
  resolveSoundPreset,
  type SoundPreset,
  type SoundTone,
} from './sound-presets';
import { usePublicConfig } from '../api/public-config';

const STORAGE_KEY = 'sl.sound.enabled';

function userExplicitPref(): boolean | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === null) return null;
    return v === '1';
  } catch {
    return null;
  }
}

function isSoundEnabled(): boolean {
  const explicit = userExplicitPref();
  if (explicit !== null) return explicit;
  return usePublicConfig.getState().config.soundsEnabledByDefault;
}

export function setSoundEnabled(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* private mode */
  }
}

export function clearUserSoundPref(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

let ctx: AudioContext | null = null;
function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    try {
      const Ctor =
        (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
          .AudioContext ??
        (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

function playTones(tones: SoundTone[]): void {
  if (tones.length === 0) return;
  if (!isSoundEnabled()) return;
  const ac = getCtx();
  if (!ac) return;
  if (ac.state === 'suspended') void ac.resume().catch(() => undefined);
  const now = ac.currentTime;
  for (const t of tones) {
    const osc = ac.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = t.freq;
    const gain = ac.createGain();
    const g = t.gain ?? 0.18;
    gain.gain.setValueAtTime(0, now + t.start);
    gain.gain.linearRampToValueAtTime(g, now + t.start + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + t.start + t.duration);
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.start(now + t.start);
    osc.stop(now + t.start + t.duration + 0.02);
  }
}

function playPresetBySlug(slug: string, fallback: string): void {
  const preset =
    resolveSoundPreset(slug) ?? resolveSoundPreset(fallback);
  if (!preset) return;
  playTones(preset.tones);
}

/** Play a preset by exact slug, ignoring the on/off toggle (admin audition button). */
export function playPresetForcedBySlug(slug: string): void {
  const preset = resolveSoundPreset(slug);
  if (!preset) return;
  playTones(preset.tones);
}

/** Play a preset object directly. */
export function playPreset(preset: SoundPreset): void {
  playTones(preset.tones);
}

export function playShareDing(): void {
  const slug = usePublicConfig.getState().config.soundShareTone;
  playPresetBySlug(slug, 'ascending');
}

export function playNotificationDing(): void {
  const slug = usePublicConfig.getState().config.soundNotificationTone;
  playPresetBySlug(slug, 'three-tone');
}

export const soundPrefs = { isSoundEnabled, setSoundEnabled, clearUserSoundPref, userExplicitPref };
